import { useState } from 'react'

import { useMailPreviewApi } from '@/common/api/modules/ugcMail'
import {
  useCreateReviewTemplateApi,
  useDeleteReviewTemplateApi,
  useReviewTemplatesApi,
  useUpdateReviewTemplateApi
} from '@/common/api/modules/ugcReviewSetup'
import { ReviewTemplate, TEMPLATE_VARIABLES } from '@/common/api/params/ugcReviewSetup'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * レビュー依頼メールの文面。
 *
 * サイトが書くのは**件名と本文だけ**。枠・★・ボタン・開封ピクセルは共通で、
 * 送信時に自動で付く。ここで HTML を書く必要はない（書いてもエスケープされる）。
 *
 * 「購入後○日」はここには置かない。間隔は［送信タイミングと文面の設定］の1か所で決める。
 * 以前はテンプレートにも日数の入力欄があり、設定できるのに送信処理は
 * 見ていなかった——効かない設定は、無いより悪い。
 */

const input =
  'shadow-inset text-size-13 w-full rounded-lg border border-border-input bg-input px-3 py-2'

type Draft = {
  id: number | null
  template_name: string
  subject: string
  body: string
}

const emptyDraft = (): Draft => ({
  id: null,
  template_name: '',
  subject: '',
  body: ''
})

const MailTemplateEditor = ({ siteId }: { siteId: number }) => {
  const { data, isLoading, mutate } = useReviewTemplatesApi({ site_id: siteId })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  // プレビュー中の文面ID。メール全体（枠・★・ボタン込み）を実物と同じ組み立てで見せる
  const [previewId, setPreviewId] = useState<number | null>(null)
  const { data: preview } = useMailPreviewApi(
    previewId !== null ? { site_id: siteId, template_id: previewId } : undefined
  )

  const done = () => {
    setDraft(null)
    setError(null)
    mutate()
  }
  const fail = (e: unknown) => setError(e instanceof Error ? e.message : '保存できませんでした')

  const { trigger: create, isMutating: creating } = useCreateReviewTemplateApi({
    onSuccess: done,
    onError: fail
  })
  const { trigger: update, isMutating: updating } = useUpdateReviewTemplateApi({
    onSuccess: done,
    onError: fail
  })
  const { trigger: remove } = useDeleteReviewTemplateApi({
    onSuccess: () => {
      setDeletingId(null)
      mutate()
    },
    onError: (e) => {
      setDeletingId(null)
      fail(e)
    }
  })

  const saving = creating || updating

  const onSave = () => {
    if (!draft) return
    if (!draft.template_name.trim()) {
      setError('文面の名前を入力してください')
      return
    }
    if (!draft.body.trim()) {
      setError('本文を入力してください')
      return
    }
    setError(null)
    if (draft.id === null) {
      create({
        site_id: siteId,
        channel: 'email',
        template_name: draft.template_name.trim(),
        subject: draft.subject.trim() || null,
        body: draft.body
      })
    } else {
      update({
        site_id: siteId,
        template_id: draft.id,
        template_name: draft.template_name.trim(),
        subject: draft.subject.trim() || null,
        body: draft.body
      })
    }
  }

  const edit = (t: ReviewTemplate) =>
    setDraft({
      id: t.id,
      template_name: t.template_name,
      subject: t.subject ?? '',
      body: t.body
    })

  const templates = data?.templates ?? []

  return (
    <Area>
      <Heading subText='件名と本文だけを書きます。枠・★の評価・ボタンは送信時に自動で付きます。'>
        文面
      </Heading>
      <AreaContent>
        {isLoading ? (
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        ) : (
          <div className='flex flex-col gap-4'>
            {templates.length === 0 && !draft && (
              <Text size={13} color={TextColor.SECONDARY}>
                文面がまだありません。作らない場合は、既定の文面で送られます。
              </Text>
            )}

            {templates.length > 0 && (
              <div className='flex flex-col gap-2'>
                {templates.map((t) => (
                  <div
                    key={t.id}
                    className='flex flex-wrap items-start gap-3 rounded-lg border border-border-primary px-4 py-3'
                  >
                    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                      <Text size={13} weight='bold'>
                        {t.template_name}
                      </Text>
                      <Text size={12} color={TextColor.SECONDARY}>
                        件名: {t.subject || '（未設定。既定の件名で送ります）'}
                      </Text>
                      <Text size={10} color={TextColor.TERTIARY}>
                        {t.body.slice(0, 60)}
                        {t.body.length > 60 ? '…' : ''}
                      </Text>
                    </div>
                    <div className='flex shrink-0 gap-2'>
                      <Button
                        color={ButtonColor.TERTIARY}
                        onClick={() => setPreviewId(previewId === t.id ? null : t.id)}
                      >
                        {previewId === t.id ? 'プレビューを閉じる' : 'プレビュー'}
                      </Button>
                      <Button color={ButtonColor.TERTIARY} onClick={() => edit(t)}>
                        編集
                      </Button>
                      <Button
                        color={ButtonColor.TERTIARY}
                        disabled={deletingId === t.id}
                        loading={deletingId === t.id}
                        onClick={() => {
                          setDeletingId(t.id)
                          remove({ site_id: siteId, template_id: t.id })
                        }}
                      >
                        削除
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {previewId !== null && preview && (
              <div className='flex flex-col gap-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  件名: {preview.subject}
                </Text>
                <div className='overflow-x-auto rounded-lg border border-border-primary'>
                  {/* 実際に送られるHTMLをそのまま描く。枠・★・ボタンが自動で付いた全体像 */}
                  <iframe
                    title='メール全体のプレビュー'
                    srcDoc={preview.html}
                    sandbox=''
                    className='h-[520px] w-full min-w-[560px] bg-white'
                  />
                </div>
              </div>
            )}

            {draft ? (
              <div className='flex flex-col gap-3 rounded-lg border border-border-brand px-4 py-4'>
                <label className='flex flex-col gap-1'>
                  <Text size={12}>文面の名前（管理用。お客様には出ません）</Text>
                  <input
                    className={input}
                    value={draft.template_name}
                    placeholder='例）購入後の初回依頼'
                    onChange={(e) => setDraft({ ...draft, template_name: e.target.value })}
                  />
                </label>
                <label className='flex flex-col gap-1'>
                  <Text size={12}>件名</Text>
                  <input
                    className={input}
                    value={draft.subject}
                    placeholder='例）{product_name}のご感想をお聞かせください'
                    onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  />
                </label>
                <label className='flex flex-col gap-1'>
                  <Text size={12}>本文</Text>
                  <textarea
                    className={`${input} min-h-[160px]`}
                    value={draft.body}
                    placeholder={'{customer_name}様\n\nこの度は{product_name}をご購入いただきありがとうございます。'}
                    onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  />
                </label>

                <div className='rounded-lg bg-fill-secondary px-3 py-2'>
                  <Text size={10} color={TextColor.SECONDARY}>
                    件名と本文で使える差し込み:{' '}
                    {TEMPLATE_VARIABLES.map((v) => `${v.key}（${v.label}）`).join(' / ')}
                  </Text>
                </div>

                <div className='flex flex-wrap items-center gap-3'>
                  <Button
                    color={ButtonColor.PRIMARY}
                    loading={saving}
                    disabled={saving}
                    onClick={onSave}
                  >
                    保存する
                  </Button>
                  <Button color={ButtonColor.TERTIARY} onClick={() => setDraft(null)}>
                    やめる
                  </Button>
                  {error && (
                    <Text size={12} color={TextColor.CRITICAL}>
                      {error}
                    </Text>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <Button color={ButtonColor.SECONDARY} onClick={() => setDraft(emptyDraft())}>
                  文面を追加する
                </Button>
                {error && (
                  <span className='ml-3'>
                    <Text size={12} color={TextColor.CRITICAL}>
                      {error}
                    </Text>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

export default MailTemplateEditor
